import axiosInstance from '../../../config/axiosInstance';

export type ProductionCapacityStatus =
  | 'ok'
  | 'backlog'
  | 'risk'
  | 'incomplete'
  | 'no_capacity';

export type ProductionScheduleOrderStatus =
  | 'on_time'
  | 'late'
  | 'already_overdue';

export type ProductionCapacityOrderDetail = {
  manufacturing_order_number: string;
  product_ref: string | null;
  pending_m: number | null;
  pending_hours: number | null;
  due_date: string | null;
  manufacturing_deadline: string | null;
  expedition_date: string | null;
  due_date_source: 'dispatch' | 'theoretical' | null;
};

export type ProductionCapacityDeadlinePoint = {
  due_date: string;
  manufacturing_deadline: string;
  orders_due_count: number;
  known_hours_orders_due_count: number;
  missing_hours_due_count: number;
  due_known_hours: number;
  backlog_orders_count: number;
  backlog_known_hours: number;
  cumulative_future_orders_count: number;
  cumulative_orders_count: number;
  cumulative_missing_hours_orders: number;
  cumulative_future_required_hours: number;
  cumulative_required_hours: number;
  available_working_days: number;
  available_capacity_hours: number;
  margin_hours: number;
  deficit_hours: number;
};

export type ProductionPressureWindow = {
  index: number;
  start_date: string;
  end_date: string;
  minimum_margin_hours: number;
  peak_deficit_hours: number;
  peak_deficit_date: string | null;
  end_margin_hours: number;
  required_hours_end: number;
  available_capacity_hours_end: number;
  future_orders_due_by_end: number;
  unknown_hours_orders_due_by_end: number;
};

export type ProductionPressureReceiverCandidate = {
  machine: string;
  known_margin_hours: number;
  unknown_hours_orders_due_count: number;
};

export type ProductionPressureProfile = {
  horizon_weeks: number;
  has_overload: boolean;
  first_overload_date: string | null;
  peak_deficit_hours: number;
  peak_deficit_date: string | null;
  peak_required_hours: number | null;
  peak_available_capacity_hours: number | null;
  future_orders_due_by_peak_count: number;
  unknown_hours_orders_due_by_peak_count: number;
  peak_pressure_orders: ProductionCapacityOrderDetail[];
  receiver_candidates: ProductionPressureReceiverCandidate[];
  backlog_orders_count: number;
  backlog_known_hours: number;
};

export type ProductionScheduleOrder = {
  position: number;
  manufacturing_order_number: string;
  product_ref: string | null;
  pending_m: number;
  pending_hours: number;
  expedition_date: string;
  date_source: 'dispatch' | 'theoretical';
  manufacturing_deadline: string;
  completion_date: string | null;
  completion_hours_from_reference: number;
  tardiness_hours: number;
  tardiness_working_days: number | null;
  status: ProductionScheduleOrderStatus;
};

export type ProductionScheduleScenario = {
  key: 'deadline_first' | 'proposed';
  label: string;
  orders_count: number;
  late_orders: number;
  future_late_orders: number;
  future_on_time_orders: number;
  future_tardiness_hours: number;
  total_incremental_tardiness_hours: number;
  max_future_tardiness_hours: number;
  completion_date: string | null;
  sequence: ProductionScheduleOrder[];
};

export type ProductionScheduleComparison = {
  is_available: boolean;
  reason: string | null;
  objective_label: string;
  eligible_orders: number;
  excluded_missing_hours: number;
  excluded_missing_date: number;
  already_overdue_orders: number;
  baseline: ProductionScheduleScenario | null;
  proposed: ProductionScheduleScenario | null;
  improvement: {
    saved_future_late_orders: number;
    saved_future_tardiness_hours: number;
    saved_total_incremental_tardiness_hours: number;
  } | null;
};

export type ProductionCapacityMachine = {
  machine: string;
  status: ProductionCapacityStatus;
  orders_count: number;
  known_hours_orders: number;
  missing_hours_orders: number;
  orders_with_date: number;
  orders_without_date: number;

  overdue_orders: number;
  overdue_known_hours_orders: number;
  overdue_missing_hours_orders: number;
  overdue_known_hours: number;
  backlog_workload_days: number | null;
  backlog_recovery_date: string | null;
  backlog_orders: ProductionCapacityOrderDetail[];

  known_pending_hours: number;
  workload_days: number | null;
  workload_weeks: number | null;
  projected_clear_date: string | null;

  first_due_date: string | null;
  last_due_date: string | null;
  first_future_due_date: string | null;
  last_future_due_date: string | null;

  first_future_risk: ProductionCapacityDeadlinePoint | null;
  first_future_risk_orders: ProductionCapacityOrderDetail[];
  minimum_future_margin_hours: number | null;
  max_future_deficit_hours: number;
  future_deadline_points: ProductionCapacityDeadlinePoint[];

  first_risk: ProductionCapacityDeadlinePoint | null;
  minimum_margin_hours: number | null;
  max_deficit_hours: number;
  deadline_points: ProductionCapacityDeadlinePoint[];

  pressure: ProductionPressureProfile;
  pressure_windows: ProductionPressureWindow[];

  scheduling: ProductionScheduleComparison;
};

export type ProductionCapacityIntelligenceResponse = {
  reference_date: string;
  snapshot: {
    id: string;
    source_file_name: string;
    published_at: string;
  } | null;
  calendar: {
    working_weekdays: number[];
    working_days_per_week: number;
    daily_capacity_hours: number;
    weekly_capacity_hours: number;
    manufacturing_deadline_buffer_working_days: number;
    active_shifts: Array<{
      code: string | null;
      label: string | null;
      start_time: string;
      end_time: string;
      duration_hours: number;
    }>;
    shift_overlap_hours: number;
    warnings: string[];
  };
  summary: {
    machines_count: number;
    machines_with_backlog: number;
    machines_at_risk: number;
    machines_incomplete: number;
    machines_without_capacity: number;
    machines_with_sequence_improvement: number;
    orders_count: number;
    known_hours_orders: number;
    missing_hours_orders: number;
    orders_without_date: number;
    known_pending_hours: number;
    overdue_orders_count: number;
    known_overdue_hours: number;
    saved_future_late_orders: number;
    saved_future_tardiness_hours: number;
    pressure_horizon_weeks: number;
    machines_with_pressure_overload: number;
    earliest_pressure_overload_date: string | null;
    maximum_pressure_deficit_hours: number;
    maximum_pressure_deficit_machine: string | null;
  };
  machines: ProductionCapacityMachine[];
};

export async function getIndustrialCostProductionCapacityIntelligence(
  referenceDate: string,
): Promise<ProductionCapacityIntelligenceResponse> {
  const { data } = await axiosInstance.get(
    '/industrial-costs/production-intelligence/capacity-risk',
    { params: { reference_date: referenceDate } },
  );

  return data as ProductionCapacityIntelligenceResponse;
}
